import { useCallback } from 'react';
// eslint-disable-next-line jira/restricted/@jira+issue__context-service
import { useAnalyticsSource } from '@jira/issue__context-service/src/useIssueKey.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { TrackEmbeddedConfluence } from './EmbeddedPageModalProps.types.tsx';
import { fireEmbeddedConfluenceAnalytics } from './fireEmbeddedConfluenceAnalytics.tsx';
import { getParentProduct } from './getParentProduct.tsx';

/**
 * @param sourceOverride Analytics surface to report instead of the issue-context
 * store value. Layout-level callers (e.g. the embedded Confluence side-panel) run
 * outside the issue/work-item provider tree, where `useAnalyticsSource()` is empty,
 * so they pass the source captured at panel-open time. Omitted by callers inside
 * the issue view, which read the source from context as before.
 */
export const useEmbeddedPageTracking = (sourceOverride?: string): TrackEmbeddedConfluence => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tenantContext = useTenantContext();
	const contextSource = useAnalyticsSource();
	const source = sourceOverride ?? contextSource;

	const trackEmbeddedConfluence: TrackEmbeddedConfluence = useCallback(
		({
			eventName,
			action,
			actionSubject,
			contentType,
			projectType,
			embeddedConfluenceSource = 'jiraIssueView',
			eventType = 'ui',
			flow,
			userHasConfluenceAccess,
			tenantHasConfluence,
			crossJoinStatus,
			originProduct,
			requestedProduct,
			isLivePage,
			linkObjectType,
			confluenceContentId,
		}) => {
			const analyticsEvent = createAnalyticsEvent({
				action,
				actionSubject: actionSubject ?? `${eventName}`,
			});

			const attributes = {
				parentProduct: getParentProduct(projectType),
				tenantEditions: tenantContext?.appEditions,
				productEntitlementDetails: tenantContext?.productEntitlementDetails,
				embeddedConfluenceSource,
				flow,
				jiraIssueSurfaceArea: source,
				userHasConfluenceAccess,
				tenantHasConfluence,
				crossJoinStatus,
				originProduct,
				requestedProduct,
				isLivePage,
				isSiteAdmin: tenantContext?.isSiteAdmin,
				linkObjectType,
				confluenceContentId,
				contentType,
			};
			fireEmbeddedConfluenceAnalytics({
				analyticsEvent,
				attributes,
				eventType,
				eventName: `${actionSubject ?? eventName} ${action}`,
			});
		},
		[source, tenantContext, createAnalyticsEvent],
	);

	return trackEmbeddedConfluence;
};
